/**
 * Het tabblad LLM Config: het model voor de teksten, de dienst voor de
 * illustraties, de voorleesstem, en de API-sleutels.
 *
 * Eerst alleen-lezen; met Bewerken wordt het een formulier. Een sleutel komt
 * nooit volledig terug van de server, alleen de laatste vier tekens. Laat je
 * een sleutelveld leeg, dan blijft de oude sleutel staan.
 *
 * De gekozen voorleesstem ging bij het opslaan niet mee naar de server, en
 * dus gold altijd de standaardstem (fout 7). Nu gaat hij mee.
 *
 * Er stonden ook een tekstdienst (Anthropic of Mistral), een Mistral-model en
 * een maximum aantal tokens. Die deden niets: alle tekst gaat naar Anthropic.
 * Ze zijn weg; de sleutel van Mistral staat nu bij het voorlezen, waar hij
 * voor is.
 *
 * Onderaan: een eigen stem maken van een opname, en elke stem
 * proefluisteren (onderdelen/EigenStem.js). Een eigen stem staat in de
 * keuzelijst bij de vaste stemmen.
 *
 * Bij het voorlezen ook: per moedertaal de vertaling voorlezen aan of uit, en
 * met welke stem (onderdelen/MoedertaalStemmen.js, stap 16).
 */
import React, { useEffect, useState } from 'react';
import { Check, Edit2, Key, RefreshCw, Save, Volume2 } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { VOORLEESSTEMMEN } from '../../../keuzelijsten';
import { EigenStem } from '../onderdelen/EigenStem';
import { MoedertaalStemmen, MoedertalenOverzicht, moedertalenVan } from '../onderdelen/MoedertaalStemmen';

const STANDAARD_MODEL = 'claude-sonnet-4-6';
const STANDAARD_VOORLEESMODEL = 'voxtral-mini-tts-2603';   // services/voorlezen.py, STANDAARD_VOORLEESMODEL

/** De voorleesmodellen die de server kent (uit /admin/llm-config), met de naam om te tonen. */
const voorleesmodellen = (config) => config.voorleesmodellen || [{ id: STANDAARD_VOORLEESMODEL, naam: 'Voxtral 2' }];
const modelnaam = (config, id) => (voorleesmodellen(config).find(m => m.id === id) || { naam: id }).naam;   // dezelfde als STANDAARD_MODEL in backend/app/instellingen.py
const STANDAARD_STEM = 'en_paul_cheerful';
const GEEN_SLEUTELS = { anthropic_api_key: '', mistral_api_key: '', openai_api_key: '', gemini_api_key: '' };

/** Het formulier, gevuld met wat er nu is ingesteld; de sleutelvelden leeg. */
/** De naam van een stem: een eigen stem, een vaste stem, of anders het id. */
function stemnaam(config, stem) {
  const eigen = (config.eigen_stemmen || []).find(s => s.id === stem);
  if (eigen) return `${eigen.naam} (eigen stem)`;
  const vast = VOORLEESSTEMMEN.find(s => s.value === stem);
  return vast ? vast.label : stem;
}

function formulierVan(config) {
  return {
    anthropic_model: config.anthropic_model || STANDAARD_MODEL,
    image_provider: config.image_provider || 'openai',
    voxtral_voice: config.voxtral_voice || STANDAARD_STEM,
    voxtral_voice_rustig: config.voxtral_voice_rustig || '',   // leeg: dezelfde als de gewone stem
    voxtral_model: config.voxtral_model || STANDAARD_VOORLEESMODEL,
    voorlezen_moedertalen: moedertalenVan(config),
    ...GEEN_SLEUTELS,
  };
}

export function Taalmodel({ actief, meldFout }) {
  const [config, setConfig] = useState({});
  const [laden, setLaden] = useState(false);
  const [bewerken, setBewerken] = useState(false);
  const [formulier, setFormulier] = useState(formulierVan({}));
  const [bezig, setBezig] = useState(false);
  const [bericht, setBericht] = useState('');

  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    api.haalTaalmodel().then(setConfig).catch(err => meldFout(err.message)).finally(() => setLaden(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  /**
   * Na een nieuwe of verwijderde eigen stem: opnieuw ophalen, en in het
   * formulier alleen wat de server veranderde (een nieuwe eigen stem wordt de
   * voorleesstem of de stem voor zijn taal; een verwijderde stem verdwijnt);
   * de rest blijft zoals de beheerder het zette, ook als het nog niet is
   * opgeslagen.
   */
  const laadOpnieuw = async () => {
    const nieuw = await api.haalTaalmodel();
    const oudeTalen = moedertalenVan(config);
    const nieuweTalen = moedertalenVan(nieuw);
    const weg = (config.eigen_stemmen || []).filter(s => !(nieuw.eigen_stemmen || []).some(n => n.id === s.id)).map(s => s.id);
    setConfig(nieuw);
    setFormulier(vorig => {
      const talen = { ...(vorig.voorlezen_moedertalen || {}) };
      Object.entries(nieuweTalen).forEach(([taal, instelling]) => {
        const inFormulier = talen[taal];
        if (!inFormulier || (oudeTalen[taal] || {}).stem !== instelling.stem || weg.includes(inFormulier.stem)) {
          talen[taal] = { aan: inFormulier ? inFormulier.aan : instelling.aan, stem: instelling.stem, stemnaam: instelling.stemnaam };
        }
      });
      // Zo ook de voorleesstem en de rustige stem: alleen als de server ze veranderde (een nieuwe Nederlandse
      // stem wordt de voorleesstem) of als de stem in het formulier net verwijderd is.
      const vanServer = (veld, leeg) => ((config[veld] || leeg) !== (nieuw[veld] || leeg) || weg.includes(vorig[veld])
        ? (nieuw[veld] || leeg) : vorig[veld]);
      return { ...vorig, voxtral_voice: vanServer('voxtral_voice', STANDAARD_STEM),
        voxtral_voice_rustig: vanServer('voxtral_voice_rustig', ''), voorlezen_moedertalen: talen };
    });
  };

  const zet = (veld) => (e) => setFormulier({ ...formulier, [veld]: e.target.value });
  const zetMoedertaal = (taal, instelling) => setFormulier(vorig => ({
    ...vorig, voorlezen_moedertalen: { ...(vorig.voorlezen_moedertalen || {}), [taal]: instelling } }));

  const begin = () => {
    setFormulier(formulierVan(config));
    setBewerken(true);
    setBericht('');
  };

  const bewaar = async (e) => {
    e.preventDefault();
    setBezig(true);
    setBericht('');
    try {
      await api.bewaarTaalmodel(formulier);
      setBericht('Configuratie opgeslagen!');
      setBewerken(false);
      setFormulier({ ...formulier, ...GEEN_SLEUTELS });
      setConfig(await api.haalTaalmodel());
    } catch (err) {
      setBericht('Fout: ' + err.message);
    } finally {
      setBezig(false);
    }
  };

  if (laden) return <p>Laden...</p>;

  return (
    <div className="card">
      <div className="flex-between mb-16">
        <h2>LLM Configuratie</h2>
        {!bewerken && (
          <button className="btn btn-primary" onClick={begin}><Edit2 size={16} /> Bewerken</button>
        )}
      </div>

      {bericht && (
        <div className={`alert ${bericht.includes('Fout') ? 'alert-error' : 'alert-success'} mb-16`}>{bericht}</div>
      )}

      {!bewerken ? <Overzicht config={config} /> : (
        <form onSubmit={bewaar}>
          <h3 className="mb-8">Tekstgeneratie (Anthropic Claude)</h3>
          <div className="card mb-16" style={{ background: 'var(--vlak)' }}>
            <div className="grid-2">
              <div className="form-group">
                <label>Anthropic Model</label>
                <input type="text" className="input" value={formulier.anthropic_model} onChange={zet('anthropic_model')}
                  autoComplete="off" spellCheck={false} aria-label="Anthropic Model" />
                <SleutelVeld naam="Anthropic API Key" isGezet={config.anthropic_api_key_set} waarde={formulier.anthropic_api_key}
                  onChange={zet('anthropic_api_key')} voorbeeld="sk-ant-..." metRuimte />
              </div>
            </div>
          </div>

          <h3 className="mb-8">Illustraties genereren</h3>
          <div className="card mb-16" style={{ background: 'var(--vlak)' }}>
            <div className="form-group">
              <label>Actieve provider</label>
              <select className="input" value={formulier.image_provider} onChange={zet('image_provider')} style={{ maxWidth: 300 }}>
                <option value="openai">OpenAI (GPT Image)</option>
                <option value="gemini">Google Gemini Imagen (werkt nog niet)</option>
              </select>
            </div>

            <div className="grid-2 mt-16">
              <div className="form-group">
                <SleutelVeld naam="OpenAI API Key" isGezet={config.openai_api_key_set} waarde={formulier.openai_api_key}
                  onChange={zet('openai_api_key')} voorbeeld="sk-..." />
                <p className="text-muted text-small mt-4">
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com</a>
                </p>
              </div>
              <div className="form-group">
                <SleutelVeld naam="Google Gemini API Key" isGezet={config.gemini_api_key_set} waarde={formulier.gemini_api_key}
                  onChange={zet('gemini_api_key')} />
                <p className="text-muted text-small mt-4">
                  <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">aistudio.google.com</a>
                </p>
              </div>
            </div>
          </div>

          <h3 className="mb-8">Voorlezen (TTS)</h3>
          <div className="card mb-16" style={{ background: 'var(--vlak)' }}>
            <p className="text-muted mb-16">
              Tekst-naar-spraak via Mistral Voxtral. Mistral volgt de taal van de tekst; een stem klinkt het best in
              de taal waarin hij is opgenomen. Een eigen stem maak je onderaan.
            </p>
            <div className="grid-2">
              <div className="form-group">
                <label htmlFor="voorleesmodel">Voorleesmodel</label>
                <select id="voorleesmodel" className="input" value={formulier.voxtral_model} onChange={zet('voxtral_model')} style={{ maxWidth: 300 }}>
                  {voorleesmodellen(config).map(m => <option key={m.id} value={m.id}>{m.naam}</option>)}
                </select>
                <p className="text-muted text-small mt-4 mb-8">
                  Voxtral 3 leest bij rustig ook langzamer (de aanwijzing [pace=slow]); luister een eigen stem eerst
                  na met Proefluisteren. Elk model maakt zijn eigen opnames; terugzetten vindt de oude weer.
                </p>
                <label htmlFor="voorleesstem">
                  <Volume2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Stem voor voorlezen
                </label>
                <select id="voorleesstem" className="input" value={formulier.voxtral_voice} onChange={zet('voxtral_voice')} style={{ maxWidth: 300 }}>
                  <StemOpties eigenStemmen={config.eigen_stemmen} />
                </select>
                <label htmlFor="voorleesstem-rustig" className="mt-8">
                  <Volume2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Stem voor rustig voorlezen
                </label>
                <select id="voorleesstem-rustig" className="input" value={formulier.voxtral_voice_rustig}
                  onChange={zet('voxtral_voice_rustig')} style={{ maxWidth: 300 }}>
                  <option value="">Dezelfde stem als hierboven</option>
                  <StemOpties eigenStemmen={config.eigen_stemmen} />
                </select>
                <p className="text-muted text-small mt-4">
                  Rustig voorlezen (de leerkracht kiest het per leerling) zet een pauze tussen de zinnen. Een eigen stem
                  van een rustig ingesproken opname maakt het tempo ook rustiger.
                </p>
              </div>
              <div className="form-group">
                <SleutelVeld naam="Mistral API Key" isGezet={config.mistral_api_key_set} waarde={formulier.mistral_api_key}
                  onChange={zet('mistral_api_key')} />
                <p className="text-muted text-small mt-4">
                  <a href="https://console.mistral.ai/api-keys" target="_blank" rel="noopener noreferrer">console.mistral.ai</a>
                </p>
              </div>
            </div>
            <MoedertaalStemmen config={config} waarde={formulier.voorlezen_moedertalen || {}} onChange={zetMoedertaal}
              gewoneStem={formulier.voxtral_voice} gewoneStemNaam={stemnaam(config, formulier.voxtral_voice)} />
          </div>

          <div className="flex gap-8">
            <button type="submit" className="btn btn-primary" disabled={bezig}>
              {bezig ? <><RefreshCw size={16} className="spinning" /> Opslaan...</> : <><Save size={16} /> Opslaan</>}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setBewerken(false)}>Annuleren</button>
          </div>
        </form>
      )}

      <EigenStem config={config} onVeranderd={laadOpnieuw} />
    </div>
  );
}

/** Alleen-lezen: wat er is ingesteld, en per sleutel of hij er is. */
function Overzicht({ config }) {
  return (
    <>
      <h3 className="mb-8">Tekstgeneratie (Anthropic Claude)</h3>
      <div className="card mb-16" style={{ background: 'var(--vlak)' }}>
        <div className="grid-2">
          <div className="form-group">
            <label>Anthropic Model</label>
            <input type="text" className="input" value={config.anthropic_model || ''} readOnly />
            <SleutelStatus isGezet={config.anthropic_api_key_set} gemaskeerd={config.anthropic_api_key_masked} metRuimte />
          </div>
        </div>
      </div>

      <h3 className="mb-8">Illustraties genereren</h3>
      <div className="card" style={{ background: 'var(--vlak)' }}>
        <div className="form-group">
          <label>Actieve provider</label>
          <span className="badge badge-primary" style={{ fontSize: 14, padding: '8px 16px' }}>
            {config.image_provider === 'gemini' ? 'Google Gemini Imagen' : 'OpenAI (GPT Image)'}
          </span>
        </div>

        <div className="grid-2 mt-16">
          <div className="form-group">
            <label>OpenAI (GPT Image)</label>
            <SleutelStatus isGezet={config.openai_api_key_set} gemaskeerd={config.openai_api_key_masked} />
          </div>
          <div className="form-group">
            <label>Google Gemini</label>
            <SleutelStatus isGezet={config.gemini_api_key_set} gemaskeerd={config.gemini_api_key_masked} />
          </div>
        </div>
      </div>

      <h3 className="mb-8 mt-24">Voorlezen (TTS)</h3>
      <div className="card" style={{ background: 'var(--vlak)' }}>
        <p className="text-muted mb-16">
          Tekst-naar-spraak via Mistral Voxtral. Leerlingen kunnen paragrafen laten voorlezen.
        </p>
        <div className="grid-2">
          <div className="form-group">
            <label>Status</label>
            <div className="flex gap-8" style={{ alignItems: 'center' }}>
              <Volume2 size={14} />
              {config.mistral_api_key_set ? (
                <span className="badge badge-success"><Check size={12} /> Beschikbaar</span>
              ) : (
                <span className="badge badge-warning">Niet beschikbaar (Mistral API key vereist)</span>
              )}
            </div>
            <SleutelStatus isGezet={config.mistral_api_key_set} gemaskeerd={config.mistral_api_key_masked} metRuimte />
          </div>
          <div className="form-group">
            <label>Stem</label>
            <input type="text" className="input" value={stemnaam(config, config.voxtral_voice || STANDAARD_STEM)} readOnly />
            <label className="mt-8">Voorleesmodel</label>
            <input type="text" className="input" value={modelnaam(config, config.voxtral_model || STANDAARD_VOORLEESMODEL)} readOnly />
            <label className="mt-8">Stem voor rustig voorlezen</label>
            <input type="text" className="input" readOnly
              value={config.voxtral_voice_rustig ? stemnaam(config, config.voxtral_voice_rustig) : 'Dezelfde als de gewone stem'} />
          </div>
        </div>
        <MoedertalenOverzicht config={config} gewoneStemNaam={stemnaam(config, config.voxtral_voice || STANDAARD_STEM)} />
      </div>
    </>
  );
}

/** De stemmen om uit te kiezen: eerst de eigen stemmen, dan de vaste (Engelse) van Mistral. */
function StemOpties({ eigenStemmen }) {
  return (
    <>
      {(eigenStemmen || []).length > 0 && (
        <optgroup label="Eigen stemmen">
          {eigenStemmen.map(stem => (
            <option key={stem.id} value={stem.id}>{stem.naam}{stem.taal && stem.taal !== 'Nederlands' ? ` (${stem.taal})` : ''}</option>
          ))}
        </optgroup>
      )}
      <optgroup label="Vaste stemmen (Engels)">
        {VOORLEESSTEMMEN.map(stem => <option key={stem.value} value={stem.value}>{stem.label}</option>)}
      </optgroup>
    </>
  );
}

/** Of een sleutel is ingesteld, met de laatste vier tekens. */
function SleutelStatus({ isGezet, gemaskeerd, metRuimte }) {
  return (
    <div className={`flex gap-8${metRuimte ? ' mt-8' : ''}`} style={{ alignItems: 'center' }}>
      <Key size={14} />
      {isGezet ? (
        <>
          <span className="badge badge-success"><Check size={12} /> Key ingesteld</span>
          <span className="text-muted">{gemaskeerd}</span>
        </>
      ) : (
        <span className="badge badge-warning">Key niet ingesteld</span>
      )}
    </div>
  );
}

/** Een veld voor een nieuwe sleutel; leeg laten houdt de oude. */
function SleutelVeld({ naam, isGezet, waarde, onChange, voorbeeld = '', metRuimte }) {
  return (
    <>
      <label className={metRuimte ? 'mt-8' : undefined}>
        <Key size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        {naam}
        {isGezet && <span className="text-muted text-small" style={{ marginLeft: 8 }}>(laat leeg om te behouden)</span>}
      </label>
      {/* new-password: anders vult een wachtwoordbeheerder hier het wachtwoord van het inloggen in */}
      <input type="password" className="input" value={waarde} onChange={onChange} placeholder={isGezet ? '••••••••' : voorbeeld}
        autoComplete="new-password" spellCheck={false} aria-label={naam} />
    </>
  );
}
